import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowUp, ArrowDown, Trash2, Plus } from 'lucide-react';
import { Switch } from './ui/switch';
import { useTierPerks, TIER_PERKS_KEY } from '../hooks/useTierPerks';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TIERS = [['standard', 'NEW'], ['silver', 'SILVER'], ['gold', 'GOLD'], ['platinum', 'PLATINUM'], ['onyx', 'ONYX'], ['black_card', 'BLACK CARD']];

function PerkRow({ perk, i, last, onChange, onMove, onDelete }) {
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`perk-row-${i}`}>
      <input value={perk.text} maxLength={80} onChange={(e) => onChange({ ...perk, text: e.target.value })}
        className="flex-1 min-w-[180px] bg-[#0C0612] border border-[#2A0F3D] focus:border-[#39FF14] font-mono text-sm px-2 py-1 text-white outline-none"
        data-testid={`perk-text-${i}`} />
      <label className="flex items-center gap-1.5 font-mono text-[10px] text-[#FFD700] uppercase">
        <Switch checked={!!perk.coming_soon} onCheckedChange={(v) => onChange({ ...perk, coming_soon: v })} data-testid={`perk-soon-${i}`} />
        Coming soon
      </label>
      <button type="button" disabled={i === 0} onClick={() => onMove(-1)} className="text-[#6B8E73] hover:text-[#39FF14] disabled:opacity-30" aria-label="Move up" data-testid={`perk-up-${i}`}><ArrowUp className="w-4 h-4" /></button>
      <button type="button" disabled={last} onClick={() => onMove(1)} className="text-[#6B8E73] hover:text-[#39FF14] disabled:opacity-30" aria-label="Move down" data-testid={`perk-down-${i}`}><ArrowDown className="w-4 h-4" /></button>
      <button type="button" onClick={onDelete} className="text-[#6B8E73] hover:text-[#FF3B30]" aria-label="Delete perk" data-testid={`perk-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
    </div>
  );
}

export function TierPerksEditor() {
  const perksMap = useTierPerks();
  const queryClient = useQueryClient();
  const [tier, setTier] = useState('standard');
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(perksMap ? perksMap[tier].map((p) => ({ ...p })) : null); }, [perksMap, tier]);
  if (!draft) return null;

  const update = (i, p) => setDraft(draft.map((x, j) => (j === i ? p : x)));
  const move = (i, d) => { const n = [...draft]; [n[i], n[i + d]] = [n[i + d], n[i]]; setDraft(n); };
  const dirty = JSON.stringify(draft) !== JSON.stringify(perksMap[tier]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API_URL}/api/admin/tier-perks/${tier}`, { perks: draft.filter((p) => p.text.trim()) }, { withCredentials: true });
      queryClient.setQueryData(TIER_PERKS_KEY, data.perks);
      toast.success('Perks saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="pt-3 border-t border-[#2A0F3D] space-y-3" data-testid="tier-perks-editor">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#6B8E73]">PERK WORDING</p>
        <p className="font-mono text-[11px] text-[#A3C7A8]">// What customers read on tier cards and the Rewards showcase. Wording only — doesn't change any rules.</p>
      </div>
      <div className="flex gap-1.5 flex-wrap">
        {TIERS.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTier(k)}
            className={`px-2.5 py-1 font-mono text-[11px] border ${k === tier ? 'border-[#FF9F0A] text-[#FF9F0A]' : 'border-[#2A0F3D] text-[#6B8E73] hover:text-[#A3C7A8]'}`}
            data-testid={`perk-tier-${k}`}>{label}</button>
        ))}
      </div>
      <div className="space-y-2">
        {draft.length === 0 && <p className="font-mono text-xs text-[#6B8E73]" data-testid="perk-empty">No perks listed for this tier.</p>}
        {draft.map((p, i) => (
          <PerkRow key={i} perk={p} i={i} last={i === draft.length - 1}
            onChange={(np) => update(i, np)} onMove={(d) => move(i, d)} onDelete={() => setDraft(draft.filter((_, j) => j !== i))} />
        ))}
      </div>
      <div className="flex gap-2">
        <button type="button" disabled={draft.length >= 8} onClick={() => setDraft([...draft, { text: '', coming_soon: false }])}
          className="inline-flex items-center gap-1 px-3 py-1.5 font-mono text-xs border border-[#2A0F3D] text-[#A3C7A8] hover:border-[#39FF14] disabled:opacity-40" data-testid="perk-add">
          <Plus className="w-3.5 h-3.5" /> Add perk
        </button>
        <button type="button" disabled={!dirty || saving} onClick={save}
          className="px-3 py-1.5 font-mono text-xs border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14]/10 disabled:opacity-40" data-testid="perk-save">
          {saving ? 'Saving…' : 'Save perks'}
        </button>
        {dirty && <button type="button" onClick={() => setDraft(perksMap[tier].map((p) => ({ ...p })))} className="px-2 font-mono text-xs text-[#6B8E73]" data-testid="perk-discard">Discard</button>}
      </div>
    </div>
  );
}
